/**
 * BillingService — SuperAdmin Yenileme / Tahsilat istemcisi.
 *
 * Veri kaynağı ana GTS DB'sidir (backend: MainDbContext). İsteklerde X-Company-Id
 * gönderilmez; erişim kontrolü sunucudaki [SuperAdminOnly] guard'ına dayanır.
 * İstek deseni CatalogService ile aynıdır: own-JWT Bearer + REACT_APP_NETAPI_URL,
 * yol /api/superadmin/billing. Tüm yazma uçları POST (PUT/DELETE yok).
 */

import ownAuthSession from './auth/ownAuthSession';

const NETAPI_URL = process.env.REACT_APP_NETAPI_URL || 'https://gtsnetapi.aquasoft.com.tr';

async function getAuthToken(): Promise<string> {
  const token = await ownAuthSession.getAccessToken();
  if (!token) throw new Error('Kullanıcı oturum açmamış');
  return token;
}

async function billingRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = await getAuthToken();
  let response: Response;
  try {
    response = await fetch(`${NETAPI_URL}/api/superadmin/billing${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  } catch {
    throw new Error(
      `Net API'ye bağlanılamadı (${NETAPI_URL}). gts_dotnetapi :5075 üzerinde çalışıyor mu?`
    );
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.message || 'Tahsilat isteği başarısız oldu');
  }
  return data as T;
}

function billingPost<T>(endpoint: string, body: unknown): Promise<T> {
  return billingRequest<T>(endpoint, { method: 'POST', body: JSON.stringify(body) });
}

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
export type BillingItemType = 'FiscalDevice' | 'Contract' | 'Device' | 'Other';
export type RenewalCaseStatus = 'Draft' | 'Active' | 'Paid' | 'Cancelled';

export interface BillingProduct {
  id: number;
  name: string;
  description?: string | null;
  defaultPrice: number;
  vatRate: number;
  currency: string;
  itemType: BillingItemType;
  isActive: boolean;
  createdAt: string;
}

export interface SaveBillingProduct {
  id?: number;
  name: string;
  description?: string | null;
  defaultPrice: number;
  vatRate: number;
  currency: string;
  itemType: BillingItemType;
  isActive: boolean;
}

export type CompanyProductKind = 'Module' | 'Custom';

export interface CompanyProduct {
  id: number;
  companyId: string;
  kind: CompanyProductKind;
  moduleId?: string | null;
  name: string;
  price: number;
  vatRate: number;
  currency: string;
  contractStartDate?: string | null;
  contractEndDate?: string | null;
  isActive: boolean;
}

export interface SaveCompanyProduct {
  id?: number;
  kind: CompanyProductKind;
  moduleId?: string | null;
  name: string;
  price: number;
  vatRate: number;
  currency: string;
  contractStartDate?: string | null;
  contractEndDate?: string | null;
  isActive: boolean;
}

export interface InvoiceIssuerCompany {
  id: number;
  title: string;
  taxNumber?: string | null;
  taxOffice?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  logoUrl?: string | null;
  /** Bağlı Aquasoft panel şirketinin FirebaseId'si. Ödeme + fatura bu şirket üzerinden yapılır. */
  linkedCompanyId?: string | null;
  /** Bağlı panel şirketinin adı (salt-okunur, sunucuda çözülür). */
  linkedCompanyName?: string | null;
  /** Bağlı panel şirketinin şirket geneli aktif kart sağlayıcısı: paytr, moka veya parampos. */
  linkedPaymentProvider?: string | null;
  /** Bağlı panel şirketinde aktif PayTR var mı? */
  linkedPaytrReady: boolean;
  paytrEnabled: boolean;
  paytrTestMode: boolean;
  paytr3DEnabled: boolean;
  paytrMerchantId?: string | null;
  paytrMerchantKey?: string | null;
  paytrMerchantSalt?: string | null;
  isActive: boolean;
}

/** Kaydetme: fatura şirketi YALNIZCA bir panel şirketi seçilerek tanımlanır (linked-only). */
export interface SaveInvoiceIssuerCompany {
  id?: number;
  linkedCompanyId: string;
  title?: string | null;
  logoUrl?: string | null;
  isActive: boolean;
}

export interface RenewalCaseLine {
  id?: number;
  billingProductId?: number | null;
  name: string;
  /** Liste fiyatına eklenen ya da düşülen tutar. Artı artırır, eksi azaltır. */
  priceAdjustment?: number;
  unitPrice: number;
  quantity: number;
  vatRate: number;
  lineTotal?: number;
}

export interface RenewalEmailLog {
  id: number;
  toEmail: string;
  daysBucket: number;
  success: boolean;
  error?: string | null;
  sentAt: string;
}

export interface RenewalCase {
  id: number;
  companyId: string;
  companyName?: string | null;
  sourceType: 'FiscalDevice' | 'Contract' | 'Manual';
  sourceId?: string | null;
  expiryDate?: string | null;
  issuerCompanyId?: number | null;
  issuerCompanyTitle?: string | null;
  status: RenewalCaseStatus;
  totalAmount: number;
  currency: string;
  paymentToken: string;
  announcementScope: 'Company' | 'Branches';
  announcementLeadDays?: number;
  branchId?: string | null;
  branchName?: string | null;
  isFranchise?: boolean;
  firmName?: string | null;
  announcementId?: number | null;
  announcementTitle?: string | null;
  announcementMessage?: string | null;
  notifyEmail?: string | null;
  notifyPhone?: string | null;
  emailSubject?: string | null;
  emailHtml?: string | null;
  paidAt?: string | null;
  paymentRef?: string | null;
  paidBy?: string | null;
  /** Fatura otomasyonu durumu: Pending | Invoiced | Sent | Emailed | Error (null = henüz yok). */
  invoiceStatus?: string | null;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  invoiceEttn?: string | null;
  invoiceError?: string | null;
  invoicedAt?: string | null;
  invoiceEmailedAt?: string | null;
  notes?: string | null;
  createdAt: string;
  lines: RenewalCaseLine[];
  emailLogs?: RenewalEmailLog[];
}

export interface SaveRenewalCase {
  id?: number;
  companyId: string;
  sourceType: 'FiscalDevice' | 'Contract' | 'Manual';
  sourceId?: string | null;
  expiryDate?: string | null;
  issuerCompanyId?: number | null;
  announcementScope: 'Company' | 'Branches';
  announcementLeadDays?: number;
  branchId?: string | null;
  announcementTitle?: string | null;
  announcementMessage?: string | null;
  notifyEmail?: string | null;
  notifyPhone?: string | null;
  emailSubject?: string | null;
  emailHtml?: string | null;
  notes?: string | null;
  lines: Array<Omit<RenewalCaseLine, 'id' | 'lineTotal'>>;
  activate?: boolean;
}

export interface InvoicePreviewParty {
  title: string;
  taxNumber?: string | null;
  taxOffice?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface InvoicePreviewLine {
  name: string;
  quantity: number;
  unit: string;
  unitPriceExclVat: number;
  vatRate: number;
  vatAmount: number;
  lineTotal: number;
}

export interface InvoicePreview {
  documentTitle: string;
  invoiceDate: string;
  invoiceNumber?: string | null;
  currency: string;
  paymentMethod: string;
  seller: InvoicePreviewParty;
  buyer: InvoicePreviewParty;
  lines: InvoicePreviewLine[];
  subTotal: number;
  vatTotal: number;
  grandTotal: number;
}

export interface RenewalCaseCounts {
  draft: number;
  active: number;
  paid: number;
  cancelled: number;
}

export interface BillingSettings {
  /** Duyurunun bitişe kaç gün kala açılacağının global varsayılanı. */
  defaultAnnouncementLeadDays: number;
}

// ─────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────
const BillingService = {
  // Settings
  async getBillingSettings(): Promise<BillingSettings> {
    const res = await billingRequest<{ data: BillingSettings }>('/settings');
    return res.data ?? { defaultAnnouncementLeadDays: 30 };
  },

  // Products
  async getProducts(includeInactive = false): Promise<BillingProduct[]> {
    const res = await billingRequest<{ data: BillingProduct[] }>(
      `/products?includeInactive=${includeInactive}`
    );
    return res.data ?? [];
  },
  async saveProduct(p: SaveBillingProduct): Promise<BillingProduct> {
    const res = await billingPost<{ data: BillingProduct }>('/products', p);
    return res.data;
  },
  async deleteProduct(id: number): Promise<void> {
    await billingPost('/products/delete', { id });
  },

  // Company products (şirkete bağlı ürün/hizmet envanteri)
  async getCompanyProducts(companyId: string): Promise<CompanyProduct[]> {
    const res = await billingRequest<{ data: CompanyProduct[] }>(
      `/companies/${encodeURIComponent(companyId)}/products`
    );
    return res.data ?? [];
  },
  async saveCompanyProducts(
    companyId: string,
    products: SaveCompanyProduct[]
  ): Promise<CompanyProduct[]> {
    const res = await billingPost<{ data: CompanyProduct[] }>(
      `/companies/${encodeURIComponent(companyId)}/products`,
      { products }
    );
    return res.data ?? [];
  },
  async deleteCompanyProduct(companyId: string, id: number): Promise<void> {
    await billingPost(`/companies/${encodeURIComponent(companyId)}/products/delete`, { id });
  },

  // Issuers
  async getIssuers(includeInactive = false): Promise<InvoiceIssuerCompany[]> {
    const res = await billingRequest<{ data: InvoiceIssuerCompany[] }>(
      `/issuers?includeInactive=${includeInactive}`
    );
    return res.data ?? [];
  },
  async saveIssuer(i: SaveInvoiceIssuerCompany): Promise<InvoiceIssuerCompany> {
    const res = await billingPost<{ data: InvoiceIssuerCompany }>('/issuers', i);
    return res.data;
  },
  async deleteIssuer(id: number): Promise<void> {
    await billingPost('/issuers/delete', { id });
  },

  // Cases
  async getCases(params: { status?: string; companyId?: string; excludePaid?: boolean } = {}): Promise<{
    data: RenewalCase[];
    counts: RenewalCaseCounts;
  }> {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.companyId) q.set('companyId', params.companyId);
    if (params.excludePaid) q.set('excludePaid', 'true');
    const qs = q.toString();
    return billingRequest<{ data: RenewalCase[]; counts: RenewalCaseCounts }>(
      `/cases${qs ? `?${qs}` : ''}`
    );
  },
  async phoneForEmail(email: string, companyId?: string): Promise<string | null> {
    const q = new URLSearchParams({ email });
    if (companyId) q.set('companyId', companyId);
    const res = await billingRequest<{ phone?: string | null }>(`/phone-for-email?${q.toString()}`);
    const phone = (res.phone || '').trim();
    return phone || null;
  },
  async getCase(id: number): Promise<RenewalCase> {
    const res = await billingRequest<{ data: RenewalCase }>(`/cases/${id}`);
    return res.data;
  },
  async saveCase(c: SaveRenewalCase): Promise<RenewalCase> {
    const res = await billingPost<{ data: RenewalCase }>('/cases', c);
    return res.data;
  },
  async activateCase(id: number): Promise<RenewalCase> {
    const res = await billingPost<{ data: RenewalCase }>('/cases/activate', { id });
    return res.data;
  },
  async cancelCase(id: number): Promise<RenewalCase> {
    const res = await billingPost<{ data: RenewalCase }>('/cases/cancel', { id });
    return res.data;
  },
  async markPaid(id: number): Promise<RenewalCase> {
    const res = await billingPost<{ data: RenewalCase }>('/cases/mark-paid', { id });
    return res.data;
  },
  async repeatCase(id: number): Promise<RenewalCase> {
    const res = await billingPost<{ data: RenewalCase }>('/cases/repeat', { id });
    return res.data;
  },
  async getInvoicePreview(id: number): Promise<InvoicePreview> {
    const res = await billingRequest<{ data: InvoicePreview }>(`/cases/${id}/invoice-preview`);
    return res.data;
  },
  /**
   * Ödenmiş kayıt için YALNIZCA fatura keser ve fatura şirketinin normal panel fatura akışına
   * düşürür. E-fatura / e-arşiv gönderimi bu akıştan yapılmaz; panelden elle yapılır.
   * Başarısız olursa { success:false, message } döner ve buton tekrar deneyebilir.
   */
  async sendInvoice(id: number): Promise<{ success: boolean; message?: string; status?: string; data: RenewalCase }> {
    return billingPost<{ success: boolean; message?: string; status?: string; data: RenewalCase }>(
      '/cases/send-invoice',
      { id }
    );
  },
  async deleteCase(id: number): Promise<void> {
    await billingPost('/cases/delete', { id });
  },

  async getDealerNotices(status?: string): Promise<DealerRenewalNotice[]> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    const res = await billingRequest<{ data: DealerRenewalNotice[] }>(`/dealer-notices${q}`);
    return res.data ?? [];
  },
  async getDealerDueItems(dealerId: number, companyId: string): Promise<DealerDueItem[]> {
    const q = new URLSearchParams({ dealerId: String(dealerId), companyId });
    const res = await billingRequest<{ data: DealerDueItem[] }>(`/dealer-notices/due-items?${q.toString()}`);
    return res.data ?? [];
  },
  async saveDealerNotice(notice: SaveDealerRenewalNotice): Promise<DealerRenewalNotice> {
    const res = await billingPost<{ data: DealerRenewalNotice }>('/dealer-notices', notice);
    return res.data;
  },
  async closeDealerNotice(id: number): Promise<DealerRenewalNotice> {
    const res = await billingPost<{ data: DealerRenewalNotice }>('/dealer-notices/close', { id });
    return res.data;
  },
  async cancelDealerNotice(id: number): Promise<DealerRenewalNotice> {
    const res = await billingPost<{ data: DealerRenewalNotice }>('/dealer-notices/cancel', { id });
    return res.data;
  },
};

export type DealerNoticeStatus = 'Active' | 'Closed' | 'Cancelled';

export interface DealerRenewalNotice {
  id: number;
  dealerId: number;
  dealerName?: string | null;
  companyId: string;
  companyName?: string | null;
  branchId?: string | null;
  branchName?: string | null;
  sourceKey: string;
  title: string;
  detail?: string | null;
  expiryDate: string;
  daysRemaining: number;
  status: DealerNoticeStatus;
  notifyEmail: string;
  notes?: string | null;
  createdAt: string;
  sentBuckets: number[];
  lastSentAt?: string | null;
}

export interface SaveDealerRenewalNotice {
  id?: number;
  dealerId: number;
  companyId: string;
  branchId?: string | null;
  branchName?: string | null;
  sourceKey?: string | null;
  title: string;
  detail?: string | null;
  expiryDate: string;
  notifyEmail: string;
  notes?: string | null;
}

export interface DealerDueItem {
  sourceKey: string;
  title: string;
  detail?: string | null;
  expiryDate: string;
  daysRemaining: number;
  branchId?: string | null;
  branchName?: string | null;
  openNoticeId?: number | null;
}

export default BillingService;
